import { CaseToExternalUserToExternalUserRole, ExternalUserRoleRole, UserRole, UserType } from '../../generated'
import { Assignee } from '../interfaceTypes'

export const formatAddress = (addresses?: ({ address1?: string | null; address2?: string | null; city?: string | null; state?: string | null; zipCode?: string | null } | null)[] | null): string | undefined => {
  const addr = addresses?.find(Boolean)
  if (!addr) return undefined
  return [addr.address1, addr.address2, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ')
}

const OPTIONAL_RESPONSIBLE_ATTORNEY_TYPES = [
  UserType.ResponsibleAttorney_3,
  UserType.ResponsibleAttorney_4,
  UserType.ResponsibleAttorney_5,
  UserType.ResponsibleAttorney_6
]

const FIXED_ASSIGNEE_TYPES = [
  UserType.OriginatorAttorney,
  UserType.ResponsibleAttorney_1,
  UserType.ResponsibleAttorney_2,
  ...OPTIONAL_RESPONSIBLE_ATTORNEY_TYPES
]

export const FIXED_ROLES: Assignee[] = [
  {
    userId: '',
    label: 'Originating Attorney',
    role: UserRole.Attorney,
    userType: UserType.OriginatorAttorney,
    hourlyRate: 0
  },
  {
    userId: '',
    label: 'Responsible Attorney 1 (Billing Attorney)',
    role: UserRole.Attorney,
    userType: UserType.ResponsibleAttorney_1,
    hourlyRate: 0
  },
  {
    userId: '',
    label: 'Responsible Attorney 2',
    role: UserRole.Attorney,
    userType: UserType.ResponsibleAttorney_2,
    hourlyRate: 0
  },
  {
    userId: '',
    label: 'Paralegal',
    role: UserRole.Paralegal,
    userType: UserType.Paralegal,
    hourlyRate: 0
  }
]

export const reorderLegalTeam = (fields: Assignee[], creatingMatter: boolean): Assignee[] => {
  const fixedAssignees: Record<UserType, Assignee | undefined> = {} as any

  // A role only survives a re-order when the set used to CAPTURE it into its fixed slot is the same set used
  // to EXCLUDE it from `otherRoles`. Paralegal used to appear in the exclusion set but not the capture set, so
  // on the create form a chosen paralegal was dropped from `otherRoles` while its slot fell back to the blank
  // FIXED_ROLES default — the selection silently vanished every time this step remounted, which happens
  // whenever a validation error moves the user to another step and they come back.
  //
  // Still conditional on `creatingMatter` so the edit form behaves exactly as before: there Paralegal is not
  // slotted, it comes through `otherRoles`, and the empty slot row is stripped by the filter at the end.
  const slottedTypes = creatingMatter ? [...FIXED_ASSIGNEE_TYPES, UserType.Paralegal] : FIXED_ASSIGNEE_TYPES

  for (const field of fields) {
    if (!slottedTypes.includes(field.userType)) continue
    // A matter can have several paralegals but there is one Paralegal slot: the first fills it.
    if (field.userType === UserType.Paralegal && fixedAssignees[UserType.Paralegal]) continue
    fixedAssignees[field.userType] = field
  }

  const fixedAttorneys = FIXED_ROLES.map(defaultRole => fixedAssignees[defaultRole.userType] || defaultRole)

  const optionalResponsibleAttorneys = fields.filter(f => OPTIONAL_RESPONSIBLE_ATTORNEY_TYPES.includes(f.userType))

  // Paralegals beyond the slotted one stay as extra rows; excluding them by type alone silently dropped them
  // (e.g. duplicating a matter with two paralegals, or adding a second one and changing step).
  const isExtraParalegal = (f: Assignee) =>
    f.userType === UserType.Paralegal && f !== fixedAssignees[UserType.Paralegal]

  const otherRoles = fields.filter(
    f =>
      (!slottedTypes.includes(f.userType) || isExtraParalegal(f)) &&
      !OPTIONAL_RESPONSIBLE_ATTORNEY_TYPES.includes(f.userType)
  )

  const result = [...fixedAttorneys, ...optionalResponsibleAttorneys, ...otherRoles]
  const alwaysVisibleTypes = [UserType.ResponsibleAttorney_1, UserType.OriginatorAttorney]
  return creatingMatter
    ? result
    : result.filter(assignee => !!assignee.userId || alwaysVisibleTypes.includes(assignee.userType))
}

export const getCasePartiesByRole = (
  caseParties: CaseToExternalUserToExternalUserRole[],
  roles: { value: string }[],
  externalUserRoleType: ExternalUserRoleRole,
  splitBillEnabled?: boolean | null
) => {
  return caseParties
    ?.filter(item => item?.caseRole && roles?.some(role => role.value === item.caseRole))
    ?.map(item => {
      const {
        caseRole,
        billDeliveryMethods,
        externalUserToExternalUserRole: { externalUser }
      } = item

      const userRoleId = externalUser?.externalUserRoles?.find(
        role => role?.externalUserRole?.role === externalUserRoleType
      )?.externalUserRole?.id

      if (userRoleId) {
        return {
          id: externalUser?.id || '',
          email: externalUser?.email || '',
          firstName: externalUser?.firstName || '',
          lastName: externalUser?.lastName || '',
          displayName: externalUser?.displayName || '',
          clientNumber: externalUser?.clientNumber || '',
          workPhone: externalUser?.phoneNumber || '',
          caseRole: caseRole!,
          isBillParticipant: !!item.splitBillShare && !!splitBillEnabled,
          wantsBillDelivery: !!billDeliveryMethods?.length,
          splitBillShare: item.splitBillShare || undefined,
          userRoleId,
          billDeliveryMethods: item?.billDeliveryMethods ?? [],
          billingAddress: item?.billingAddress ?? undefined,
          billingPhoneNumber: item?.billingPhoneNumber ?? undefined,
          billingEmail: item?.billingEmail ?? undefined,
          defaultBillingAddress: formatAddress(externalUser?.addresses)
        }
      }
      return undefined
    })
    ?.filter(Boolean)
}
